import { useState, useEffect, useRef } from 'react';
import API from './api';

// ── Constants ──────────────────────────────────────────────────────────────────

const STAGES = [
  { key: 'parsing',     label: 'Parse Excel',    pct: [0, 18]  },
  { key: 'classifying', label: 'AI Classify',    pct: [18, 65] },
  { key: 'synthesizing',label: 'Synthesize',     pct: [65, 85] },
  { key: 'generating',  label: 'Generate Excel', pct: [85, 100]},
];

const PRIORITY_CFG = {
  HIGH:   { color: '#e8001d', bg: 'rgba(232,0,29,0.08)',   border: 'rgba(232,0,29,0.3)'   },
  MEDIUM: { color: '#e07b2a', bg: 'rgba(224,123,42,0.08)', border: 'rgba(224,123,42,0.3)' },
  LOW:    { color: '#2a6fdb', bg: 'rgba(42,111,219,0.08)', border: 'rgba(42,111,219,0.3)' },
};

const CATEGORY_CFG = {
  ISM:    { color: '#2a6fdb' },
  ISPS:   { color: '#a855f7' },
  MLC:    { color: '#22c55e' },
  SOLAS:  { color: '#e8001d' },
  MARPOL: { color: '#14b8a6' },
  STCW:   { color: '#f59e0b' },
  OTHER:  { color: '#8a9ab0' },
};

// ── Token estimation ───────────────────────────────────────────────────────────
// Synthesis output tokens ≈ 50 per deficiency (conservative).
// max_tokens for synthesis = 32000.
const TOKEN_PER_ROW   = 50;   // estimated synthesis output tokens per deficiency
const TOKEN_MAX       = 32000; // current synthesis max_tokens
const TOKEN_SAFE      = 16000; // below this → green
const TOKEN_CAUTION   = 28000; // below this → yellow, above → red

function calcTokenEstimate(rowCount) {
  const est = rowCount * TOKEN_PER_ROW;
  const pct = Math.min(100, Math.round((est / TOKEN_MAX) * 100));
  const status = est <= TOKEN_SAFE ? 'safe' : est <= TOKEN_CAUTION ? 'caution' : 'risk';
  return { est, pct, status };
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function stageIndex(key) {
  const i = STAGES.findIndex(s => s.key === key);
  return i === -1 ? 0 : i;
}

function fmtK(n) {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`;
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function PSCGuard({ setPage, onLogout }) {
  const [phase, setPhase]         = useState('idle');   // idle | running | done | error
  const [job, setJob]             = useState(null);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState(null);
  const [file, setFile]           = useState(null);
  const [dragging, setDragging]   = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showGuide, setShowGuide]   = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [filterPriority, setFilterPriority] = useState('ALL');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [elapsed, setElapsed]     = useState(0);
  const [eta, setEta]             = useState(null);
  const pollRef                   = useRef(null);
  const fileInputRef              = useRef(null);
  const jobRef                    = useRef(null);
  const stageStartRef             = useRef(null);
  const prevStageRef              = useRef(null);

  jobRef.current = job;

  useEffect(() => () => clearInterval(pollRef.current), []);

  // Reset stage timer when stage changes
  useEffect(() => {
    if (job?.stage && job.stage !== prevStageRef.current) {
      prevStageRef.current = job.stage;
      stageStartRef.current = Date.now();
      setElapsed(0);
      setEta(null);
    }
  }, [job?.stage]);

  // 1-second tick for elapsed time + ETA
  useEffect(() => {
    if (phase !== 'running') {
      setElapsed(0);
      setEta(null);
      return;
    }
    const interval = setInterval(() => {
      const j = jobRef.current;
      const start = stageStartRef.current;
      if (!start) return;
      const e = Math.floor((Date.now() - start) / 1000);
      setElapsed(e);
      if (j?.stage === 'classifying' && j?.batches_done > 0 && j?.total_batches > 0) {
        const rate = e / j.batches_done;
        const rem = Math.ceil((j.total_batches - j.batches_done) * rate);
        setEta(rem > 0 ? rem : 0);
      } else {
        setEta(null);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  // ── Polling ────────────────────────────────────────────────────────────────
  const startPolling = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await API.get(`/psc-guard/progress/${jobId}`);
        const j = res.data;
        setJob(j);
        if (j.status === 'done') {
          clearInterval(pollRef.current);
          setResult(j.result);
          setPhase('done');
        } else if (j.status === 'error') {
          clearInterval(pollRef.current);
          setError(j.error);
          setPhase('error');
        }
      } catch (e) {
        if (e.response?.status === 401) { onLogout(); clearInterval(pollRef.current); }
      }
    }, 1500);
  };

  // ── Upload & start ──────────────────────────────────────────────────────────
  const handleAnalyze = async () => {
    if (!file) return;
    setPhase('running');
    setJob(null);
    setResult(null);
    setError(null);
    setShowDetail(false);
    setExpandedItem(null);
    setFilterPriority('ALL');
    setFilterCategory('ALL');

    const form = new FormData();
    form.append('file', file);

    try {
      const res = await API.post('/psc-guard/analyze', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      startPolling(res.data.job_id);
    } catch (e) {
      if (e.response?.status === 401) { onLogout(); return; }
      setError({
        stage_label: 'Upload',
        type: 'NetworkError',
        message: e.response?.data?.detail || e.message,
        detail: null,
      });
      setPhase('error');
    }
  };

  // ── File pick ───────────────────────────────────────────────────────────────
  const handleFilePick = (f) => {
    if (!f) return;
    const ext = f.name.toLowerCase();
    if (!ext.endsWith('.xlsx') && !ext.endsWith('.xls') && !ext.endsWith('.xlsm')) {
      alert('Only Excel files (.xlsx, .xls, .xlsm) are accepted.');
      return;
    }
    setFile(f);
    setPhase('idle');
    setResult(null);
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFilePick(f);
  };

  // ── Download ────────────────────────────────────────────────────────────────
  const handleDownload = () => {
    if (!result?.checklist_filename) return;
    const token = localStorage.getItem('token');
    window.open(`/api/psc-guard/download/${result.checklist_filename}?token=${token}`, '_blank');
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const progress     = job?.progress ?? 0;
  const curStageKey  = job?.stage ?? 'parsing';
  const curStageIdx  = curStageKey === 'done' ? STAGES.length : stageIndex(curStageKey);

  const checklist    = result?.checklist ?? [];
  const categories   = result ? Object.keys(result.categories || {}) : [];
  const filteredList = checklist.filter(item =>
    (filterPriority === 'ALL' || item.priority === filterPriority) &&
    (filterCategory === 'ALL' || item.category === filterCategory)
  );

  // Token estimate: use exact total_rows if known, else rough estimate from file size
  const knownRows    = job?.total_rows ?? (result?.total_deficiencies ?? null);
  const estRows      = knownRows ?? (file ? Math.round(file.size / 650) : null);
  const tokenInfo    = estRows != null ? calcTokenEstimate(estRows) : null;

  return (
    <div style={s.wrapper}>

      {/* ── Header ── */}
      <header style={s.header}>
        <button style={s.backBtn} onClick={() => setPage('dashboard')}>← Back</button>
        <div style={s.headerTitle}>PSC Guard</div>
        <button style={s.guideBtn} onClick={() => setShowGuide(true)}>? How to Guide</button>
        <div style={s.aiBadge}>AI-POWERED</div>
      </header>

      {/* ── How to Guide Modal ── */}
      {showGuide && (
        <div style={s.overlay} onClick={() => setShowGuide(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span style={s.modalTitle}>How to Use PSC Guard</span>
              <button style={s.modalClose} onClick={() => setShowGuide(false)}>✕</button>
            </div>
            <div style={s.modalBody}>

              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>What is PSC Guard?</div>
                <div style={s.guideSectionText}>
                  PSC Guard is an AI-powered Port State Control deficiency analyzer. Upload your PSC
                  deficiency Excel report (Paris MOU, Tokyo MOU, USCG, or internal format) and the
                  AI will classify every deficiency, identify recurring patterns, and generate a
                  prioritised pre-inspection checklist.
                </div>
              </div>

              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>What You Need</div>
                <ul style={s.guideNotes}>
                  <li>A PSC deficiency report in Excel format (<strong>.xlsx / .xls / .xlsm</strong>)</li>
                  <li>The file should contain deficiency descriptions — action taken, code, and remarks columns are optional but improve accuracy</li>
                  <li>No minimum or maximum row count, but files with 300+ deficiencies may take 2–3 minutes</li>
                </ul>
              </div>

              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>Step-by-Step Workflow</div>
                <ol style={s.guideSteps}>
                  <li><strong>Upload Excel</strong> — Drag and drop your PSC deficiency file onto the upload zone, or click to browse. The file is validated immediately</li>
                  <li><strong>Check Token Estimate</strong> — A gauge bar shows the estimated synthesis token usage. Green = safe, Yellow = caution, Red = may be truncated. Large files (&gt;640 deficiencies) approach the limit</li>
                  <li><strong>Click ANALYZE</strong> — Four AI stages run in sequence: Parse Excel → AI Classify → Synthesize → Generate Excel. Each stage shows a countdown timer</li>
                  <li><strong>Review Results</strong> — The checklist is grouped by priority (HIGH / MEDIUM / LOW) and regulation category (ISM, ISPS, MLC, SOLAS, MARPOL, STCW). Use the filter buttons to narrow results</li>
                  <li><strong>Download Checklist</strong> — Click the download button to get the pre-inspection checklist as a formatted Excel file ready for distribution</li>
                </ol>
              </div>

              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>Regulation Categories</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    { label: 'ISM',    color: '#2a6fdb' },
                    { label: 'ISPS',   color: '#a855f7' },
                    { label: 'MLC',    color: '#22c55e' },
                    { label: 'SOLAS',  color: '#e8001d' },
                    { label: 'MARPOL', color: '#14b8a6' },
                    { label: 'STCW',   color: '#f59e0b' },
                  ].map(({ label, color }) => (
                    <div key={label} style={{ padding: '5px 14px', borderRadius: 20, border: `1px solid ${color}40`, background: `${color}15`, color, fontSize: 12, fontWeight: 700, fontFamily: 'monospace' }}>
                      {label}
                    </div>
                  ))}
                </div>
              </div>

              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>Important Notes</div>
                <ul style={s.guideNotes}>
                  <li>The <strong>AI Classify</strong> stage runs in parallel batches — the countdown timer shows estimated remaining time per batch</li>
                  <li>The token estimate is based on ~50 output tokens per deficiency. Files with &gt;320 deficiencies enter the caution zone</li>
                  <li>Results are not saved on the server — download the Excel before closing or navigating away</li>
                  <li>For best results, ensure deficiency descriptions in the Excel are in English</li>
                </ul>
              </div>

            </div>
          </div>
        </div>
      )}

      <main style={s.main}>

        {/* ── Upload card ── */}
        <div style={s.uploadCard}>
          <div style={s.uploadCardLeft}>
            <div style={s.sectionLabel}>PSC DEFICIENCY EXCEL</div>
            <div style={s.uploadDesc}>
              Upload your PSC deficiency report (Paris MOU, Tokyo MOU, USCG or internal format).
              The AI agent will classify every deficiency by ISM / ISPS / MLC / SOLAS / MARPOL,
              identify recurring patterns, and generate a prioritised pre-inspection checklist.
            </div>
          </div>

          {/* Drop zone */}
          <div
            style={{
              ...s.dropZone,
              borderColor: dragging ? '#c9a84c' : file ? '#22c55e' : '#1e2d40',
              background:  dragging ? 'rgba(201,168,76,0.05)' : file ? 'rgba(34,197,94,0.04)' : '#0d1b2a',
            }}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.xlsm"
              style={{ display: 'none' }}
              onChange={e => handleFilePick(e.target.files[0])}
            />
            {file ? (
              <>
                <div style={{ fontSize: 22, marginBottom: 6 }}>✓</div>
                <div style={{ ...s.dropMain, color: '#22c55e' }}>{file.name}</div>
                <div style={s.dropSub}>
                  {(file.size / 1024).toFixed(0)} KB · click to replace
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 26, marginBottom: 6, color: '#1e2d40' }}>⬆</div>
                <div style={s.dropMain}>Drop Excel file here</div>
                <div style={s.dropSub}>.xlsx / .xls / .xlsm · click to browse</div>
              </>
            )}
          </div>

          <button
            style={{
              ...s.runBtn,
              opacity: (!file || phase === 'running') ? 0.4 : 1,
              cursor:  (!file || phase === 'running') ? 'not-allowed' : 'pointer',
            }}
            onClick={handleAnalyze}
            disabled={!file || phase === 'running'}
          >
            {phase === 'running' ? 'ANALYZING…' : 'ANALYZE'}
          </button>
        </div>

        {/* ── Token estimate panel ── */}
        {tokenInfo && (
          <div style={{
            ...s.tokenCard,
            borderColor: tokenInfo.status === 'safe'    ? 'rgba(34,197,94,0.25)'
                       : tokenInfo.status === 'caution' ? 'rgba(201,168,76,0.3)'
                       : 'rgba(232,0,29,0.3)',
          }}>
            <div style={s.tokenLeft}>
              <div style={s.sectionLabel}>TOKEN ESTIMATE</div>
              <div style={s.tokenDesc}>
                {knownRows != null
                  ? `${knownRows} deficiencies detected`
                  : `~${estRows} deficiencies estimated from file size`}
                {' · '}Synthesis output: ~{fmtK(tokenInfo.est)} / {fmtK(TOKEN_MAX)} tokens
              </div>
            </div>

            {/* Gauge bar */}
            <div style={s.tokenGaugeWrap}>
              <div style={s.tokenGaugeTrack}>
                <div style={{
                  ...s.tokenGaugeFill,
                  width: `${tokenInfo.pct}%`,
                  background: tokenInfo.status === 'safe'    ? '#22c55e'
                            : tokenInfo.status === 'caution' ? '#c9a84c'
                            : '#e8001d',
                }} />
                {/* Safe zone marker at 50% */}
                <div style={{ ...s.tokenMarker, left: '50%', borderColor: 'rgba(34,197,94,0.5)' }} title="Safe limit" />
                {/* Caution marker at 87.5% */}
                <div style={{ ...s.tokenMarker, left: '87.5%', borderColor: 'rgba(232,0,29,0.5)' }} title="Risk limit" />
              </div>
              <div style={s.tokenGaugeLabels}>
                <span>0</span><span style={{ marginLeft: '47%' }}>{fmtK(TOKEN_SAFE)}</span><span style={{ marginLeft: 'auto' }}>{fmtK(TOKEN_MAX)}</span>
              </div>
            </div>

            {/* Status badge */}
            <div style={{
              ...s.tokenStatus,
              color:       tokenInfo.status === 'safe'    ? '#22c55e'
                         : tokenInfo.status === 'caution' ? '#c9a84c'
                         : '#e8001d',
              borderColor: tokenInfo.status === 'safe'    ? 'rgba(34,197,94,0.3)'
                         : tokenInfo.status === 'caution' ? 'rgba(201,168,76,0.3)'
                         : 'rgba(232,0,29,0.3)',
              background:  tokenInfo.status === 'safe'    ? 'rgba(34,197,94,0.07)'
                         : tokenInfo.status === 'caution' ? 'rgba(201,168,76,0.07)'
                         : 'rgba(232,0,29,0.07)',
            }}>
              {tokenInfo.status === 'safe'    ? '● SAFE'
             : tokenInfo.status === 'caution' ? '▲ CAUTION'
             : '✕ RISK'}
            </div>
          </div>
        )}

        {/* ── Progress panel ── */}
        {(phase === 'running' || phase === 'done' || phase === 'error') && (
          <div style={s.progressCard}>

            {/* Stage dots */}
            <div style={s.stepsRow}>
              {STAGES.map((st, idx) => {
                const done   = curStageIdx > idx || phase === 'done';
                const active = curStageIdx === idx && phase === 'running';
                const failed = phase === 'error' && curStageIdx === idx;
                return (
                  <div key={st.key} style={s.stepItem}>
                    <div style={{
                      ...s.stepDot,
                      background:  failed ? '#e8001d' : done ? '#22c55e' : active ? '#c9a84c' : '#1e2d40',
                      borderColor: failed ? '#e8001d' : done ? '#22c55e' : active ? '#c9a84c' : '#1e2d40',
                      boxShadow:   active ? '0 0 10px rgba(201,168,76,0.5)' : 'none',
                    }}>
                      {done && !failed ? '✓' : failed ? '✕' : idx + 1}
                    </div>
                    <div style={{ ...s.stepLabel, color: (done || active) ? '#e8edf4' : '#4a5a70' }}>
                      {st.label}
                    </div>
                    {idx < STAGES.length - 1 && (
                      <div style={{
                        ...s.stepConnector,
                        background: (curStageIdx > idx || phase === 'done') ? '#22c55e' : '#1e2d40',
                      }} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bar */}
            <div style={s.barTrack}>
              <div style={{
                ...s.barFill,
                width: `${progress}%`,
                background: phase === 'error' ? '#e8001d' : phase === 'done' ? '#22c55e' : '#c9a84c',
                transition: 'width 0.4s ease',
              }} />
            </div>

            {/* Status line */}
            <div style={s.statusRow}>
              <span style={{
                fontSize: 11, fontFamily: 'monospace',
                color: phase === 'error' ? '#e8001d' : phase === 'done' ? '#22c55e' : '#c9a84c',
              }}>
                {phase === 'error' ? '✕ FAILED' : phase === 'done' ? '✓ COMPLETE' : '● RUNNING'}
              </span>
              <span style={s.statusMsg}>{job?.message ?? ''}</span>
              <span style={{ marginLeft: 'auto', fontSize: 11, fontFamily: 'monospace', color: '#8a9ab0' }}>
                {progress}%
              </span>
            </div>

            {/* Info row: deficiency count + batch progress + timer */}
            {phase === 'running' && (
              <div style={s.infoRow}>
                {job?.total_rows > 0 && (
                  <span style={s.infoBadge}>
                    {job.total_rows} deficiencies loaded
                  </span>
                )}
                {job?.stage === 'classifying' && job?.total_batches > 0 && (
                  <span style={s.infoBadge}>
                    Batch {job.batches_done ?? 0} / {job.total_batches}
                  </span>
                )}
                <span style={{ marginLeft: 'auto', fontSize: 11, fontFamily: 'monospace', color: '#8a9ab0' }}>
                  {elapsed}s elapsed
                  {eta !== null && (
                    <span style={{ color: '#c9a84c' }}> · ~{eta}s remaining</span>
                  )}
                </span>
              </div>
            )}
          </div>
        )}

        {/* ── Error ── */}
        {phase === 'error' && error && (
          <div style={s.errorCard}>
            <div style={s.errorHeader}>
              <div>
                <div style={s.errorTitle}>Analysis Failed</div>
                <div style={s.errorSubtitle}>
                  Stage: <strong>{error.stage_label}</strong>
                  {' · '}Type: <strong>{error.type}</strong>
                </div>
              </div>
              <button style={s.detailToggle} onClick={() => setShowDetail(v => !v)}>
                {showDetail ? 'Hide trace' : 'Show trace'}
              </button>
            </div>
            <div style={s.errorMsg}>{error.message}</div>
            {showDetail && error.detail && (
              <pre style={s.errorTrace}>{error.detail}</pre>
            )}
          </div>
        )}

        {/* ── Results ── */}
        {phase === 'done' && result && (
          <>
            {/* Parse warning */}
            {result.parse_warning && (
              <div style={s.warnBox}>⚠  {result.parse_warning}</div>
            )}

            {/* Stats strip */}
            <div style={s.statsRow}>
              {[
                { label: 'DEFICIENCIES',    value: result.total_deficiencies,              color: null  },
                { label: 'CHECKLIST ITEMS', value: result.checklist_items,                 color: '#c9a84c' },
                { label: 'VESSELS',         value: result.vessels_count,                   color: null  },
                { label: 'HIGH PRIORITY',   value: result.priorities?.HIGH ?? 0,           color: '#e8001d' },
                { label: 'MEDIUM PRIORITY', value: result.priorities?.MEDIUM ?? 0,         color: '#e07b2a' },
                { label: 'LOW PRIORITY',    value: result.priorities?.LOW ?? 0,            color: '#2a6fdb' },
              ].map(({ label, value, color }) => (
                <div key={label} style={s.statCard}>
                  <div style={{ ...s.statValue, color: color || '#e8edf4' }}>{value ?? '—'}</div>
                  <div style={s.statLabel}>{label}</div>
                </div>
              ))}

              <button style={s.downloadBtn} onClick={handleDownload}>
                ↓ Download Excel
              </button>
            </div>

            {/* Category breakdown */}
            {result.categories && Object.keys(result.categories).length > 0 && (
              <div style={s.catRow}>
                <span style={s.catRowLabel}>BY CATEGORY</span>
                {Object.entries(result.categories)
                  .sort((a, b) => b[1] - a[1])
                  .map(([cat, count]) => (
                    <div key={cat} style={{
                      ...s.catChip,
                      borderColor: (CATEGORY_CFG[cat] || CATEGORY_CFG.OTHER).color,
                      color:       (CATEGORY_CFG[cat] || CATEGORY_CFG.OTHER).color,
                    }}>
                      {cat} <span style={s.catCount}>{count}</span>
                    </div>
                  ))}
              </div>
            )}

            {/* Filters */}
            <div style={s.filterRow}>
              <span style={s.filterLabel}>FILTER</span>
              {['ALL', 'HIGH', 'MEDIUM', 'LOW'].map(p => (
                <button
                  key={p}
                  style={{
                    ...s.filterBtn,
                    background: filterPriority === p
                      ? (p === 'ALL' ? '#1e2d40' : PRIORITY_CFG[p]?.color || '#1e2d40')
                      : 'transparent',
                    color: filterPriority === p ? '#fff' : '#8a9ab0',
                    borderColor: filterPriority === p
                      ? (p === 'ALL' ? '#1e2d40' : PRIORITY_CFG[p]?.color || '#1e2d40')
                      : '#1e2d40',
                  }}
                  onClick={() => setFilterPriority(p)}
                >{p}</button>
              ))}
              <span style={{ ...s.filterLabel, marginLeft: 16 }}>CATEGORY</span>
              {['ALL', ...categories].map(c => (
                <button
                  key={c}
                  style={{
                    ...s.filterBtn,
                    background: filterCategory === c
                      ? ((CATEGORY_CFG[c] || {}).color || '#1e2d40')
                      : 'transparent',
                    color: filterCategory === c ? '#fff' : '#8a9ab0',
                    borderColor: filterCategory === c
                      ? ((CATEGORY_CFG[c] || {}).color || '#1e2d40')
                      : '#1e2d40',
                  }}
                  onClick={() => setFilterCategory(c)}
                >{c}</button>
              ))}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#4a5a70', fontFamily: 'monospace' }}>
                {filteredList.length} / {checklist.length} items
              </span>
            </div>

            {/* Checklist items */}
            {filteredList.length === 0 && (
              <div style={s.emptyState}>No checklist items match the current filter.</div>
            )}

            {filteredList.map((item, idx) => {
              const pcfg  = PRIORITY_CFG[item.priority] || PRIORITY_CFG.LOW;
              const ccfg  = CATEGORY_CFG[item.category] || CATEGORY_CFG.OTHER;
              const open  = expandedItem === idx;
              return (
                <div key={idx} style={{ ...s.checkItem, borderLeftColor: pcfg.color }}>
                  {/* Row header */}
                  <div style={s.checkItemHeader} onClick={() => setExpandedItem(open ? null : idx)}>
                    <div style={s.checkItemLeft}>
                      <span style={{ ...s.priorityBadge, color: pcfg.color, borderColor: pcfg.border }}>
                        {item.priority}
                      </span>
                      <span style={{ ...s.categoryBadge, color: ccfg.color }}>
                        {item.category}
                      </span>
                      <span style={s.regRef}>{item.regulation_ref}</span>
                    </div>
                    <div style={s.checkItemRight}>
                      <span style={{ ...s.occBadge, color: pcfg.color }}>
                        {item.occurrence_count}×
                      </span>
                      <span style={{ fontSize: 11, color: '#4a5a70' }}>{open ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {/* Check item text */}
                  <div style={s.checkItemText}>{item.check_item}</div>

                  {/* Expanded detail */}
                  {open && (
                    <div style={s.checkItemDetail}>
                      {item.verification_method && (
                        <div style={s.detailBlock}>
                          <div style={s.detailBlockLabel}>VERIFICATION METHOD</div>
                          <div style={s.detailBlockText}>{item.verification_method}</div>
                        </div>
                      )}
                      <div style={s.detailRow}>
                        {item.responsible_party && (
                          <div style={s.detailChip}>
                            <span style={s.detailChipLabel}>RESPONSIBLE</span>
                            <span style={s.detailChipVal}>{item.responsible_party}</span>
                          </div>
                        )}
                        {item.vessels_affected?.length > 0 && (
                          <div style={s.detailChip}>
                            <span style={s.detailChipLabel}>VESSELS</span>
                            <span style={s.detailChipVal}>{item.vessels_affected.join(', ')}</span>
                          </div>
                        )}
                      </div>
                      {item.related_deficiencies?.length > 0 && (
                        <div style={s.detailBlock}>
                          <div style={s.detailBlockLabel}>RELATED DEFICIENCIES ({item.related_deficiencies.length})</div>
                          <ul style={s.relList}>
                            {item.related_deficiencies.map((d, di) => (
                              <li key={di} style={s.relItem}>{d}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Bottom download */}
            {checklist.length > 6 && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <button style={s.downloadBtnLg} onClick={handleDownload}>
                  ↓ Download Full Checklist (Excel)
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const s = {
  wrapper: { minHeight: '100vh', background: '#0d1b2a', color: '#e8edf4', fontFamily: 'system-ui, sans-serif' },

  header: { background: '#111d2e', borderBottom: '1px solid #1e2d40', padding: '0 40px', height: 64, display: 'flex', alignItems: 'center', gap: 20 },
  backBtn: { background: 'transparent', border: '1px solid #1e2d40', color: '#8a9ab0', padding: '6px 16px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'monospace' },
  headerTitle: { fontSize: 15, fontWeight: 700, flex: 1 },
  aiBadge: { background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '4px 12px', borderRadius: 12, fontSize: 10, letterSpacing: 1, fontFamily: 'monospace' },

  main: { padding: 40, maxWidth: 1000, margin: '0 auto' },

  uploadCard: { background: '#111d2e', border: '1px solid #1e2d40', borderRadius: 12, padding: '24px 28px', display: 'flex', alignItems: 'center', gap: 20, marginBottom: 20, flexWrap: 'wrap' },
  uploadCardLeft: { flex: 1, minWidth: 240 },
  sectionLabel: { fontSize: 10, fontWeight: 700, letterSpacing: 2, fontFamily: 'monospace', color: '#c9a84c', marginBottom: 8 },
  uploadDesc: { fontSize: 13, color: '#8a9ab0', lineHeight: 1.65 },

  dropZone: { border: '2px dashed', borderRadius: 10, padding: '20px 24px', cursor: 'pointer', textAlign: 'center', minWidth: 200, transition: 'all 0.2s' },
  dropMain: { fontSize: 13, fontWeight: 600, color: '#e8edf4', marginBottom: 4 },
  dropSub:  { fontSize: 11, color: '#4a5a70' },

  runBtn: { background: '#e8001d', border: 'none', borderRadius: 8, padding: '14px 28px', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, whiteSpace: 'nowrap', fontFamily: 'monospace', transition: 'opacity 0.2s' },

  // Progress
  progressCard: { background: '#111d2e', border: '1px solid #1e2d40', borderRadius: 12, padding: '24px 28px', marginBottom: 20 },
  stepsRow: { display: 'flex', alignItems: 'center', marginBottom: 20 },
  stepItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', flex: 1 },
  stepDot: { width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#e8edf4', border: '2px solid', transition: 'all 0.3s', marginBottom: 8, zIndex: 1 },
  stepLabel: { fontSize: 10, fontFamily: 'monospace', textAlign: 'center', letterSpacing: 0.5, transition: 'color 0.3s' },
  stepConnector: { position: 'absolute', top: 15, left: '50%', width: '100%', height: 2, transition: 'background 0.3s' },
  barTrack: { height: 6, background: '#0d1b2a', borderRadius: 3, overflow: 'hidden', marginBottom: 12 },
  barFill:  { height: '100%', borderRadius: 3 },
  statusRow: { display: 'flex', alignItems: 'center', gap: 10 },
  statusMsg: { fontSize: 11, color: '#8a9ab0', fontFamily: 'monospace' },
  infoRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' },
  infoBadge: { fontSize: 10, fontFamily: 'monospace', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c', padding: '3px 10px', borderRadius: 10 },

  // Error
  errorCard: { background: 'rgba(232,0,29,0.06)', border: '1px solid rgba(232,0,29,0.25)', borderRadius: 12, padding: '20px 24px', marginBottom: 20 },
  errorHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  errorTitle: { fontSize: 14, fontWeight: 700, color: '#ff6b7a', marginBottom: 4 },
  errorSubtitle: { fontSize: 12, color: '#8a9ab0' },
  detailToggle: { background: 'transparent', border: '1px solid rgba(232,0,29,0.3)', color: '#ff6b7a', padding: '4px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: 'monospace' },
  errorMsg: { fontSize: 13, color: '#e8edf4', lineHeight: 1.6, marginBottom: 8 },
  errorTrace: { background: '#0d1b2a', border: '1px solid #1e2d40', borderRadius: 8, padding: 12, fontSize: 11, color: '#8a9ab0', overflowX: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', lineHeight: 1.5 },

  // Warning
  warnBox: { background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 8, padding: '12px 16px', color: '#c9a84c', fontSize: 13, marginBottom: 16 },

  // Stats
  statsRow: { display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' },
  statCard: { background: '#111d2e', border: '1px solid #1e2d40', borderRadius: 10, padding: '14px 16px', textAlign: 'center', minWidth: 84 },
  statValue: { fontSize: 26, fontWeight: 800, fontFamily: 'monospace' },
  statLabel: { fontSize: 9, color: '#8a9ab0', letterSpacing: 1.4, marginTop: 4, fontFamily: 'monospace' },
  downloadBtn: { marginLeft: 'auto', background: 'transparent', border: '1px solid #c9a84c', color: '#c9a84c', padding: '10px 20px', borderRadius: 8, fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: 'pointer', fontFamily: 'monospace', whiteSpace: 'nowrap' },
  downloadBtnLg: { background: '#c9a84c', border: 'none', color: '#0d1b2a', padding: '14px 40px', borderRadius: 10, fontSize: 12, fontWeight: 800, letterSpacing: 1.5, cursor: 'pointer', fontFamily: 'monospace' },

  // Category row
  catRow: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 },
  catRowLabel: { fontSize: 9, fontWeight: 700, letterSpacing: 2, color: '#4a5a70', fontFamily: 'monospace' },
  catChip: { border: '1px solid', borderRadius: 20, padding: '3px 10px', fontSize: 10, fontWeight: 700, fontFamily: 'monospace', display: 'flex', gap: 5, alignItems: 'center' },
  catCount: { opacity: 0.7 },

  // Filter
  filterRow: { display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 },
  filterLabel: { fontSize: 9, fontWeight: 700, letterSpacing: 2, color: '#4a5a70', fontFamily: 'monospace' },
  filterBtn: { border: '1px solid', borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'monospace', transition: 'all 0.15s' },

  emptyState: { textAlign: 'center', color: '#8a9ab0', padding: 48, fontSize: 14, background: '#111d2e', border: '1px solid #1e2d40', borderRadius: 12 },

  // Checklist item
  checkItem: { background: '#111d2e', border: '1px solid #1e2d40', borderLeft: '3px solid', borderRadius: 10, marginBottom: 8, overflow: 'hidden' },
  checkItemHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', cursor: 'pointer', gap: 12 },
  checkItemLeft: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  checkItemRight: { display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 },
  checkItemText: { padding: '0 16px 14px', fontSize: 13, lineHeight: 1.65, color: '#e8edf4' },
  priorityBadge: { border: '1px solid', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, fontFamily: 'monospace' },
  categoryBadge: { fontSize: 10, fontWeight: 700, fontFamily: 'monospace', letterSpacing: 0.5 },
  regRef: { fontSize: 11, color: '#c9a84c', fontFamily: 'monospace', fontStyle: 'italic' },
  occBadge: { fontSize: 14, fontWeight: 800, fontFamily: 'monospace' },

  checkItemDetail: { borderTop: '1px solid #1e2d40', padding: '14px 16px', background: 'rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: 12 },
  detailBlock: { display: 'flex', flexDirection: 'column', gap: 6 },
  detailBlockLabel: { fontSize: 9, fontWeight: 700, letterSpacing: 2, color: '#4a5a70', fontFamily: 'monospace' },
  detailBlockText: { fontSize: 12, color: '#8a9ab0', lineHeight: 1.6 },
  detailRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  detailChip: { background: '#0d1b2a', border: '1px solid #1e2d40', borderRadius: 8, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 3 },
  detailChipLabel: { fontSize: 9, fontWeight: 700, letterSpacing: 2, color: '#4a5a70', fontFamily: 'monospace' },
  detailChipVal: { fontSize: 12, color: '#e8edf4' },
  relList: { margin: 0, padding: '0 0 0 16px', display: 'flex', flexDirection: 'column', gap: 3 },
  relItem: { fontSize: 12, color: '#4a5a70', lineHeight: 1.5 },

  // Token estimate panel
  tokenCard: { background: '#111d2e', border: '1px solid', borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' },
  tokenLeft: { minWidth: 200, flex: 1 },
  tokenDesc: { fontSize: 12, color: '#8a9ab0', marginTop: 4, fontFamily: 'monospace' },
  tokenGaugeWrap: { flex: 2, minWidth: 200 },
  tokenGaugeTrack: { height: 8, background: '#0d1b2a', borderRadius: 4, overflow: 'visible', position: 'relative', marginBottom: 4 },
  tokenGaugeFill: { height: '100%', borderRadius: 4, transition: 'width 0.4s ease' },
  tokenMarker: { position: 'absolute', top: -3, width: 2, height: 14, borderLeft: '2px dashed', opacity: 0.6 },
  tokenGaugeLabels: { display: 'flex', fontSize: 9, color: '#4a5a70', fontFamily: 'monospace' },
  tokenStatus: { border: '1px solid', borderRadius: 8, padding: '6px 14px', fontSize: 10, fontWeight: 700, fontFamily: 'monospace', letterSpacing: 1, whiteSpace: 'nowrap' },

  // Guide modal
  guideBtn: { background: 'transparent', border: '1px solid rgba(201,168,76,0.4)', color: '#c9a84c', padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'monospace', letterSpacing: 0.5 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { background: '#111d2e', border: '1px solid #1e2d40', borderRadius: 14, width: '100%', maxWidth: 740, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #1e2d40', flexShrink: 0 },
  modalTitle: { fontSize: 16, fontWeight: 700, color: '#e8edf4' },
  modalClose: { background: 'transparent', border: '1px solid #1e2d40', color: '#8a9ab0', width: 30, height: 30, borderRadius: 6, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalBody: { overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 },
  guideSection: {},
  guideSectionTitle: { fontSize: 12, fontWeight: 700, letterSpacing: 1.5, color: '#c9a84c', fontFamily: 'monospace', marginBottom: 10, textTransform: 'uppercase' },
  guideSectionText: { fontSize: 13, color: '#8a9ab0', lineHeight: 1.7 },
  guideSteps: { margin: 0, paddingLeft: 20, color: '#8a9ab0', fontSize: 13, lineHeight: 1.9 },
  guideNotes: { margin: 0, paddingLeft: 20, color: '#8a9ab0', fontSize: 13, lineHeight: 1.9 },
};
